// Shared, lightweight animation helpers (framer-motion).
// UI-only: every animation settles on its natural/final state, so removing
// these changes nothing about layout or behaviour. Global reduced-motion is
// respected via <MotionConfig reducedMotion="user"> in App.js.
import { useEffect, useState } from "react";
import { animate, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

/**
 * Entrance props for a card in a grid/list. Staggers by index but caps the
 * total delay so large grids (e.g. 90+ studies) don't crawl in for seconds.
 *
 * Opacity-only on purpose: these cards keep their existing Tailwind
 * `hover:-translate-y-*` lift, and animating `y` here would leave an inline
 * transform that overrides that hover. Fading preserves the hover effect.
 */
export const cardEntrance = (i = 0) => ({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.35, ease: EASE, delay: Math.min(i * 0.03, 0.45) },
});

/** Fade + rise, intended for use with whileInView on long pages. */
export const fadeRise = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: EASE } },
};

/** Modal backdrop + panel variants (match EditStudyModal's feel). */
export const modalBackdrop = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

export const modalPanel = {
  initial: { y: 18, scale: 0.98, opacity: 0 },
  animate: { y: 0, scale: 1, opacity: 1 },
  exit: { y: 18, scale: 0.98, opacity: 0 },
  transition: { type: "spring", stiffness: 260, damping: 22 },
};

/**
 * Counts a number up on mount. Non-numeric values (e.g. "—") render as-is.
 * Honours prefers-reduced-motion by skipping the count and showing the value.
 */
export const AnimatedNumber = ({
  value,
  suffix = "",
  duration = 0.9,
  className,
}) => {
  const reduce = useReducedMotion();
  const numeric = typeof value === "number" ? value : parseFloat(value);
  const isNum = Number.isFinite(numeric);
  const [display, setDisplay] = useState(isNum && !reduce ? 0 : numeric);

  useEffect(() => {
    if (!isNum || reduce) {
      setDisplay(numeric);
      return;
    }
    const controls = animate(0, numeric, {
      duration,
      ease: EASE,
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [numeric, isNum, reduce, duration]);

  if (!isNum) return <span className={className}>{value}</span>;
  return (
    <span className={className}>
      {Math.round(display)}
      {suffix}
    </span>
  );
};
